import { useState, useCallback, useRef, useEffect, useMemo } from 'react';

export const useOIMonitor = () => {
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [marketData, setMarketData] = useState([]);
  const [alertData, setAlertData] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [symbols, setSymbols] = useState([]);
  const [monitorCount, setMonitorCount] = useState(0);
  const [nextScanCountdown, setNextScanCountdown] = useState(0);
  const [config, setConfig] = useState({
    changeThreshold: 10,
    volumeThreshold: 20,
    interval: 5
  });

  const intervalRef = useRef(null);
  const countdownRef = useRef(null);
  const lastUpdateRef = useRef(0);

  // Optimized countdown timer with throttling
  useEffect(() => {
    if (isMonitoring && nextScanCountdown > 0) {
      countdownRef.current = setInterval(() => {
        const now = Date.now();
        // Throttle updates to prevent excessive re-renders
        if (now - lastUpdateRef.current >= 950) { // Update roughly every second
          setNextScanCountdown(prev => {
            if (prev <= 1) {
              return 0;
            }
            return prev - 1;
          });
          lastUpdateRef.current = now;
        }
      }, 1000);

      return () => {
        if (countdownRef.current) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
        }
      };
    }
  }, [isMonitoring, nextScanCountdown > 0]); // Only depend on boolean state

  const fetchSymbolData = useCallback(async (symbol) => {
    try {
      const oiResponse = await fetch(`https://fapi.binance.com/futures/data/openInterestHist?symbol=${symbol}&period=5m&limit=20`);

      if (!oiResponse.ok) {
        throw new Error(`HTTP Error - OI: ${oiResponse.status}`);
      }

      const oiData = await oiResponse.json();

      if (!oiData || oiData.length < 2) return null;

      const openInterests = oiData.map(item => parseFloat(item.sumOpenInterest));
      const volumes = oiData.map(item => parseFloat(item.sumOpenInterestValue));
      const timestamps = oiData.map(item => parseInt(item.timestamp));

      const latest = openInterests[openInterests.length - 1];
      const previous = openInterests[openInterests.length - 2];
      const latestVolume = volumes[volumes.length - 1];
      const previousVolume = volumes[volumes.length - 2];
      
      const oiChangePercent = previous !== 0 ? ((latest - previous) / previous) * 100 : 0;
      const ma10 = openInterests.slice(-10).reduce((sum, val) => sum + val, 0) / Math.min(10, openInterests.length);
      const deviationFromMa = ma10 !== 0 ? ((latest - ma10) / ma10) * 100 : 0;
      
      const avgVolume = volumes.length >= 6 ? volumes.slice(-6, -1).reduce((sum, val) => sum + val, 0) / 5 : latestVolume;
      const oiVolumeChange = avgVolume !== 0 ? ((latestVolume - avgVolume) / avgVolume) * 100 : 0;
      const volumeChange5m = previousVolume !== 0 ? ((latestVolume - previousVolume) / previousVolume) * 100 : 0;

      const currentPrice = latest !== 0 ? latestVolume / latest : 0;
      const previousPrice = previous !== 0 ? previousVolume / previous : currentPrice;
      const priceChangePercent = previousPrice !== 0 ? ((currentPrice - previousPrice) / previousPrice) * 100 : 0;

      return {
        symbol,
        openInterest: latestVolume,
        openInterestCount: latest,
        oiChangePercent,
        deviationFromMa,
        oiVolumeChange,
        changePercent: priceChangePercent,
        price: currentPrice,
        volume: latestVolume,
        volumeChange: volumeChange5m,
        timestamp: timestamps[timestamps.length - 1],
        avgVolume,
        deviation: deviationFromMa
      };
    } catch (error) {
      console.error(`Error fetching data for ${symbol}:`, error);
      return null;
    }
  }, []);

  const detectAnomalies = useCallback((item) => {
    const reasons = [];
    const { changeThreshold, volumeThreshold } = config;
    
    if (Math.abs(item.oiChangePercent) > changeThreshold) {
      reasons.push(`OI change ${item.oiChangePercent > 0 ? '+' : ''}${item.oiChangePercent.toFixed(2)}%`);
    }
    if (Math.abs(item.deviationFromMa) > changeThreshold) {
      reasons.push(`MA deviation ${item.deviationFromMa > 0 ? '+' : ''}${item.deviationFromMa.toFixed(2)}%`);
    }
    if (Math.abs(item.oiVolumeChange) > volumeThreshold) {
      reasons.push(`OI volume anomaly ${item.oiVolumeChange > 0 ? '+' : ''}${item.oiVolumeChange.toFixed(2)}%`);
    }
    
    return reasons;
  }, [config]);

  const sendDiscordAlert = useCallback(async (item, anomalies) => {
    const webhookUrl = localStorage.getItem('discordWebhook');
    if (!webhookUrl) {
      console.log('⚠️ Discord webhook URL not set. Skipping alert.');
      return;
    }
    
    try {
      const embed = {
        title: `🚨 Open Interest Alert`,
        description: `**${item.symbol}** Anomaly detected`,
        color: 15548997,
        fields: [
          {
            name: 'Anomaly Reasons',
            value: anomalies.join('\n') || 'Unknown',
            inline: false
          },
          {
            name: 'Open Interest (USDT)',
            value: (item.openInterest / 1000000).toFixed(2) + 'M',
            inline: true
          },
          {
            name: 'OI Change',
            value: `${item.oiChangePercent > 0 ? '+' : ''}${item.oiChangePercent.toFixed(2)}%`,
            inline: true
          },
          {
            name: 'Price Change (24h)',
            value: `${item.changePercent > 0 ? '+' : ''}${item.changePercent.toFixed(2)}%`,
            inline: true
          },
          {
            name: 'MA Deviation',
            value: `${item.deviationFromMa > 0 ? '+' : ''}${item.deviationFromMa.toFixed(2)}%`,
            inline: true
          },
          {
            name: 'Current Price',
            value: `${item.price.toFixed(4)}`,
            inline: true
          },
          {
            name: '24h Volume',
            value: `${(item.volume / 1000000).toFixed(2)}M`,
            inline: true
          }
        ],
        timestamp: new Date().toISOString(),
        footer: { text: 'Open Interest Monitoring System' }
      };

      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          embeds: [embed]
        })
      });

      if (!response.ok) {
        throw new Error(`Discord API responded with status: ${response.status}`);
      }

      console.log(`✅ Discord alert sent successfully: ${item.symbol}`);
      return true;
    } catch (error) {
      console.error('❌ Failed to send Discord alert:', error);
      return false;
    }
  }, []);

  const testDiscordWebhook = useCallback(async () => {
    const webhookUrl = localStorage.getItem('discordWebhook');
    if (!webhookUrl) {
      throw new Error('Discord webhook URL not configured');
    }
    
    const testData = {
      symbol: 'BTCUSDT',
      openInterest: 125000000,
      oiChangePercent: 12.5,
      changePercent: -2.3,
      deviationFromMa: 15.2,
      price: 43250.75,
      volume: 850000000
    };

    const testAnomalies = ['Test alert - OI change +12.5%', 'MA deviation +15.2%'];
    
    return await sendDiscordAlert(testData, testAnomalies);
  }, [sendDiscordAlert]);

  const loadSymbols = useCallback(async () => {
    try {
      const response = await fetch('https://fapi.binance.com/fapi/v1/exchangeInfo');
      const data = await response.json();
      const symbolList = data.symbols
        .filter(symbol => 
          symbol.quoteAsset === 'USDT' && 
          symbol.status === 'TRADING' && 
          symbol.contractType === 'PERPETUAL'
        )
        .map(symbol => symbol.symbol)
        .sort();
      setSymbols(symbolList);
      console.log(`✅ Loaded ${symbolList.length} USDT perpetual pairs`);
    } catch (error) {
      console.error('❌ Failed to load symbols:', error);
    }
  }, []);

  const init = useCallback(async () => {
    console.log('👁️ Initializing OI Monitor...');
    await loadSymbols();
  }, [loadSymbols]);

  const performScan = useCallback(async () => {
    if (symbols.length === 0) return;

    console.log(`🚀 Starting analysis for ${symbols.length} symbols...`);
    
    const fetched = [];
    const alerts = [];

    const batchSize = 10;
    for (let i = 0; i < symbols.length; i += batchSize) {
      const batch = symbols.slice(i, i + batchSize);
      const batchPromises = batch.map(symbol => fetchSymbolData(symbol));
      const batchResults = await Promise.all(batchPromises);
      
      for (const data of batchResults) {
        if (!data) continue;
        
        const anomalies = detectAnomalies(data);
        
        data.status = anomalies.length > 0 ? 'alert' : 'normal';
        data.anomalyReason = anomalies.join(', ');
        
        if (anomalies.length > 0) {
          await sendDiscordAlert(data, anomalies);
          alerts.push({ ...data, anomalies });
        }
        
        fetched.push(data);
      }
      
      if (i + batchSize < symbols.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      console.log(`📊 Processed ${Math.min(i + batchSize, symbols.length)}/${symbols.length} symbols`);
    }

    fetched.sort((a, b) => b.openInterest - a.openInterest);
    
    setMarketData(fetched);
    setAlertData(alerts);
    console.log(`✅ Completed analysis for ${fetched.length} symbols. Found ${alerts.length} anomalies.`);
  }, [symbols, fetchSymbolData, detectAnomalies, sendDiscordAlert]);

  const start = useCallback(async () => {
    if (isMonitoring) return;
    setIsMonitoring(true);
    setMonitorCount(prev => prev + 1);

    await performScan();

    const intervalMinutes = config.interval;
    const intervalSeconds = intervalMinutes * 60;
    setNextScanCountdown(intervalSeconds);

    intervalRef.current = setInterval(async () => {
      await performScan();
      setNextScanCountdown(intervalSeconds);
    }, intervalSeconds * 1000);

  }, [isMonitoring, config.interval, performScan]);

  const stop = useCallback(() => {
    setIsMonitoring(false);
    setNextScanCountdown(0);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    console.log('⏹️ Stopped OI monitoring');
  }, []);

  const refreshData = useCallback(async () => {
    if (!isMonitoring) return;
    
    console.log('🔄 Manual refresh requested...');
    await performScan();
    
    const intervalSeconds = config.interval * 60;
    setNextScanCountdown(intervalSeconds);
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(async () => {
        await performScan();
        setNextScanCountdown(intervalSeconds);
      }, intervalSeconds * 1000);
    }
  }, [isMonitoring, config.interval, performScan]);

  const updateConfig = useCallback((newConfig) => {
    setConfig(prev => ({ ...prev, ...newConfig }));
    
    if (isMonitoring && newConfig.interval && newConfig.interval !== config.interval) {
      const newIntervalSeconds = newConfig.interval * 60;
      setNextScanCountdown(newIntervalSeconds);
      
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = setInterval(async () => {
          await performScan();
          setNextScanCountdown(newIntervalSeconds);
        }, newIntervalSeconds * 1000);
      }
    }
  }, [isMonitoring, config.interval, performScan]);

  const getActiveMonitorCount = useCallback(() => isMonitoring ? 1 : 0, [isMonitoring]);

  // Memoized countdown formatter to prevent unnecessary re-renders
  const formatCountdown = useMemo(() => {
    return (seconds) => {
      if (seconds <= 0) return '00:00';
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    };
  }, []);

  return {
    isMonitoring,
    marketData,
    alertData,
    alerts,
    symbols,
    monitorCount,
    config,
    nextScanCountdown,
    formatCountdown,
    init,
    start,
    stop,
    refreshData,
    updateConfig,
    getActiveMonitorCount,
    testDiscordWebhook
  };
};